import {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import { Box, Button, Flex, Text } from "@chakra-ui/react";
import LabelComponent from "../../atom/Label/LabelComponent";
import { applyFilters } from "../../../helpers/applyFilters";
import SearchArea from "../../atom/Search/SearchArea";
import { Filter } from "../../../types/General/Filter";
import { Dayjs } from "dayjs";

interface SearchAreaTemplateProps {
  setDataTable: (data: any) => void;
  dataTable: any[];
  searchTitle: string;
  children?: ReactNode;
  filterFunction?: (
    item: any,
    searchTerm: string,
    filters: Record<string, string | undefined>,
  ) => boolean;
  handleCleanFilters?: () => void;
  onSubmit?: () => void;
  filters?: Filter[];
  keepActiveFilters?: Filter[];
  selectedFilterItem?: string;
  setSelectedFilterItem?: (data: any) => void;
  hasInputSearch?: boolean;
  setIsSearching?: Dispatch<SetStateAction<boolean>>;
  isSearching?: boolean;
  totalItems?: number;
  setDates?: ((date: Dayjs | null) => void)[];
}

const SearchAreaTemplate: FC<SearchAreaTemplateProps> = ({
  setDataTable,
  dataTable = [],
  searchTitle,
  children,
  filterFunction,
  filters = [],
  keepActiveFilters = [],
  selectedFilterItem,
  setSelectedFilterItem,
  hasInputSearch = true,
  setIsSearching,
  totalItems,
  setDates,
}) => {
  const [originalDataTable, setOriginalDataTable] = useState<any[]>(dataTable);
  const [searchResultText, setSearchResultText] = useState<string>("");
  const [inputSearch, setInputSearch] = useState<string>("");
  const [currentItemCount, setCurrentItemCount] = useState<number>(0);
  const [searchTerms, setSearchTerms] = useState<string>("");

  useEffect(() => {
    setOriginalDataTable(dataTable);
  }, [dataTable]);

  const handleSearchCall = useCallback(() => {
    const filterObject = filters.reduce(
      (acc, filter) => {
        acc[filter.key] = filter.value;
        return acc;
      },
      {} as Record<string, string | undefined>,
    );

    const filteredData = filterFunction
      ? applyFilters(
          originalDataTable,
          inputSearch,
          filterFunction,
          filterObject,
        )
      : dataTable;

    if (!filterFunction && hasInputSearch && filters.length > 0) {
      const lastFilter = filters[filters.length - 1];
      lastFilter.setValue(inputSearch);
      filterObject[lastFilter.key] = inputSearch;
    }

    setDataTable(filteredData);
    setCurrentItemCount(filteredData.length);

    setSearchTerms(
      [inputSearch, selectedFilterItem].filter(Boolean).join(", "),
    );

    setIsSearching && setIsSearching(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputSearch, filters, originalDataTable, setDataTable, filterFunction]);

  useEffect(() => {
    const resultCount =
      totalItems !== undefined ? totalItems : currentItemCount;
    if (searchTerms) {
      const summaryText =
        resultCount > 0
          ? `${resultCount} resultado(s) de: ${searchTerms}`
          : `Nenhum resultado para: ${searchTerms}`;

      setSearchResultText(summaryText);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerms, totalItems]);

  const handleCleanFilters = () => {
    setInputSearch("");
    filters.forEach((filter) => {
      const isKept = keepActiveFilters.some(
        (activeFilter) => activeFilter.key === filter.key,
      );

      if (!isKept) {
        filter.setValue("");
      }
    });
    setDates?.forEach((setDate) => setDate(null));
    setDataTable(originalDataTable);
    setSelectedFilterItem?.("");
    setSearchResultText("");
    setSearchTerms("");
    setIsSearching && setIsSearching(true);
  };

  return (
    <Box>
      <LabelComponent title={searchTitle} fontWeight="500" fontSize="md" />
      <Flex direction="column" gap={8}>
        <SearchArea
          handleSearchCall={handleSearchCall}
          setInputSearch={setInputSearch}
          inputSearch={inputSearch}
          hasInputSearch={hasInputSearch}
        >
          {children}
        </SearchArea>
        <Flex alignItems="center" justifyContent="space-between">
          <Flex direction="row" gap={4}>
            <Button
              variant="secondary"
              size="md"
              fontWeight="bold"
              onClick={handleSearchCall}
            >
              Pesquisar
            </Button>

            <Button
              variant="secondary"
              size="md"
              fontWeight="bold"
              onClick={handleCleanFilters}
            >
              Limpar
            </Button>
          </Flex>
          {searchResultText && <Text size="md">{searchResultText}</Text>}
        </Flex>
      </Flex>
    </Box>
  );
};

export default SearchAreaTemplate;
